import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import React, {ReactNode} from 'react';
import _ from 'underscore';

import * as clipboard from 'util/Clipboard';
import {ElementMouseEvent} from 'util/Types';
import uniqueId from 'util/uniqueId';

const NOTIFICATION_DEFAULT_DURATION = 30;

interface ClipboardProps {
  children: ReactNode;
  notification?: string;
  notificationDuration?: number;
  notifyCopied?: () => void;
  onClick: (e?: ElementMouseEvent) => void;
  value?: string;
}

interface ClipboardState {
  key: string;
}

class Clipboard extends React.Component<ClipboardProps, ClipboardState> {
  clipboard: clipboard.ClipboardProxy | null = null;
  DEFAULT_NOTIFY_COPIED_FN = clipboard.defaultNotifyCopied;
  rootRef = React.createRef<HTMLElement>();

  static defaultProps = {
    onClick: noop,
  };

  constructor(props: ClipboardProps) {
    super(props);
    this.state = {
      key: uniqueId('clipboard-'),
    };
  }

  setUpGlue = (): void => {
    this.clipboard = clipboard.glue(
      this.rootRef.current!,
      // eslint-disable-next-line no-restricted-syntax
      _.extend(
        {
          click: this.props.onClick,
          dataTransform: () => this.props.value,
          notifyCopied: this._notifyCopied,
        }, // eslint-disable-next-line no-restricted-syntax
        _.omit(this.props, 'children', 'component', 'onClick', 'value')
      )
    );
  };

  tearDownGlue = (): void => {
    clipboard.unglue(this.clipboard!);
  };

  override componentDidMount(): void {
    this.setUpGlue();
  }

  override componentWillUnmount(): void {
    this.tearDownGlue();
  }

  override componentDidUpdate(prevProps: ClipboardProps): void {
    if (prevProps.notifyCopied !== this.props.notifyCopied) {
      this.tearDownGlue();
      this.setUpGlue();
    }
  }

  _notifyCopied = (): void => {
    const notifications = require('notifications').default;
    if (this.props.notification) {
      const duration = this.props.notificationDuration || NOTIFICATION_DEFAULT_DURATION;
      notifications.success(this.props.notification, {duration});
    }
  };

  override render(): JSX.Element {
    return (
      <span
        className="u-pointer"
        data-target={this.state.key}
        ref={this.rootRef}
        {...getTestProps({component: 'Clipboard', element: 'root'})}
      >
        {this.props.children}
      </span>
    );
  }
}

export default Clipboard;
